import notification from "antd/es/notification";
import { defineMessage } from "react-intl";
import { call, put, select, takeEvery, takeLatest } from "redux-saga/effects";
import * as uuid from "uuid";

import EntityType from "@mapmycustomers/shared/enum/EntityType";
import Activity from "@mapmycustomers/shared/types/entity/Activity";
import { RawFile } from "@mapmycustomers/shared/types/File";
import Organization from "@mapmycustomers/shared/types/Organization";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";
import ViewState from "@mapmycustomers/shared/types/viewModel/ViewState";

import { showGlobalCreateActivityModal } from "@app/component/createEditEntity/Activity/store/actions";
import getSuccessNotificationNode from "@app/component/createEditEntity/util/getSuccessNotificationNode";
import { mergeNewFilterModelsOnRootLevel } from "@app/component/view/utils/mergeFilterModels";
import i18nService from "@app/config/I18nService";
import localSettings from "@app/config/LocalSettings";
import { BulkDeleteResponse } from "@app/store/api/ApiService";
import { callApi } from "@app/store/api/callApi";
import parseBulkDeleteOutcome from "@app/store/api/utils/parseBulkDeleteOutcome";
import { handleError } from "@app/store/errors/actions";
import { exportEntities } from "@app/store/exportEntities/actions";
import { getOrganization, getOrganizationId } from "@app/store/iam";
import { notifyAboutChanges } from "@app/store/uiSync/actions";
import FileListItem from "@app/types/FileListItem";
import getParentActivitiesFilter from "@app/util/activity/getParentActivitiesFilter";
import { allSettled, SettleResult } from "@app/util/effects";
import activityFieldModel, { ActivityFieldName } from "@app/util/fieldModel/ActivityFieldModel";
import { isCalculatedField, isCustomField } from "@app/util/fieldModel/impl/assert";
import {
  createEmptyFilterModel,
  extractSimpleConditionForFieldWithinTag,
} from "@app/util/filters/utils";
import { activityLayoutModel } from "@app/util/layout/impl";
import { convertToPlatformSortModel } from "@app/util/viewModel/convertSort";
import { convertToPlatformFilterModel } from "@app/util/viewModel/convertToPlatformFilterModel";

import activitySettings from "../utils/activitySettings";

import {
  applyListViewSettings,
  bulkDeleteActivities,
  bulkEdit,
  bulkPostpone,
  bulkToggleComplete,
  deleteActivity,
  exportActivities,
  fetchChildActivities,
  fetchFilePreview,
  fetchList,
  openDuplicationModal,
  removeActivityFile,
  toggleComplete,
  updateActivity,
  updateActivityView,
  updateNote,
  uploadActivityFiles,
} from "./actions";
import {
  getListViewState,
  getTotalFilteredRecordsCount,
  getUpdateViewStateCallback,
} from "./selectors";

const postponeSuccessMessage = defineMessage({
  id: "scene.activity.saga.postpone.success",
  defaultMessage: "Activity postponed successfully",
  description: "Activity postpone success message",
});

const bulkPostponeSuccessMessage = defineMessage({
  id: "scene.activity.saga.bulkPostpone.success",
  defaultMessage:
    "{count} {count, plural, one {activity} other {activities}} postponed by {period}",
  description: "Activity bulk postpone success message",
});

const toggleCompleteSuccessMessage = defineMessage({
  id: "scene.activity.saga.toggleComplete.success",
  defaultMessage: 'Activity{completed, select, true {} other { is no longer}} marked as "Done"',
  description: "Activity toggle complete success message",
});

const bulkToggleCompleteSuccessMessage = defineMessage({
  id: "scene.activity.saga.bulkToggleComplete.success",
  defaultMessage:
    '{count} {count, plural, one {activity} other {activities}}{completed, select, true {} other { {count, plural, one {is} other {are}} no longer}} marked as "Done"',
  description: "Activity bulk toggle complete success message",
});

const filesAddedSuccess = defineMessage({
  id: "scene.activity.saga.fileAdded.success",
  defaultMessage: "{count, plural, one {File} other {Files}} Added to Activity",
  description: "Activity files added success message",
});

const noteAddedSuccess = defineMessage({
  id: "scene.activity.saga.noteAdded.success",
  defaultMessage: "Activity Note {text}",
  description: "Activity note added success message",
});

const deleteSuccessMessage = defineMessage({
  id: "scene.activity.saga.delete.success.title",
  defaultMessage: "Activity successfully deleted",
  description: "Activity delete success message",
});

const successDeletedDescription = defineMessage({
  id: "scene.activity.saga.delete.success.description",
  defaultMessage: "To restore this record, go to Data in Settings.",
  description: "Activity deleted success message description",
});

const bulkDeleteSuccessMessage = defineMessage({
  id: "scene.activity.saga.bulkDelete.success.title",
  defaultMessage: "{count, plural, one {Activity} other {Activities}} successfully deleted",
  description: "Activities bulk delete success message",
});

const bulkDeleteSuccessDescription = defineMessage({
  id: "scene.activity.saga.bulkDelete.success.description",
  defaultMessage:
    "To restore {plural, select, true {these records} other {this record}}, go to Data in Settings.",
  description: "Activities are deleted successfully description from the Select Bar",
});

const bulkDeleteFailedMessage = defineMessage({
  id: "scene.activity.saga.bulkDelete.error.title",
  defaultMessage: "{count} {count, plural, one {activity was} other {activities were}} not deleted",
  description: "None of the selected activities could be deleted",
});

const bulkDeletePartialMessage = defineMessage({
  id: "scene.activity.saga.bulkDelete.warning.title",
  defaultMessage:
    "Only {count} {count, plural, one {activity} other {activities}} deleted successfully",
  description: "Activities partially deleted successfully",
});

const bulkDeletePartialDescription = defineMessage({
  id: "scene.activity.saga.bulkDelete.warning.description",
  defaultMessage:
    "{failedCount, plural, one {# activity} other {# activities}} could not be deleted. {failedCount, plural, one {It may have} other {They may have}} already been deleted, or you may not have permission to delete {failedCount, plural, one {it} other {them}}.",
  description: "Activities partially deleted successfully description",
});

const bulkEditSuccessMessage = defineMessage({
  id: "scene.activity.saga.bulkEdit.success",
  defaultMessage: "{count} {count, plural, one {activity} other {activities}} updated",
  description: "Activities bulk delete edit message",
});

const getRequestFlags = () => {
  const showSharedActivities = activitySettings.getSharedActivitiesVisibility();

  return {
    includeAccessStatus: true,
    includeCustomFields: true,
    includeEmails: true,
    includeFiles: true,
    includeUsersWithAccess: true,
    ...(showSharedActivities
      ? {
          includeAccountSharedActivities: true,
          includeContactSharedActivities: true,
          includeDealSharedActivities: true,
        }
      : {}),
  };
};

export function* onFetchList({ payload }: ReturnType<typeof fetchList.request>) {
  try {
    const isGroupChildrenRequest = payload.request.groupKeys?.length === 1;

    if (!payload.fetchOnlyWithoutFilters && !isGroupChildrenRequest) {
      yield put(applyListViewSettings(payload.request));
    }

    const listViewState: ViewState = yield select(getListViewState);

    if (!payload.fetchOnlyWithoutFilters && !isGroupChildrenRequest) {
      localSettings.setViewSettings(listViewState, "activity/listView");
    }

    if (payload.updateOnly) {
      return;
    }

    const $offset =
      (payload.fetchOnlyWithoutFilters || isGroupChildrenRequest) && payload.request.range
        ? payload.request.range.startRow
        : listViewState.range.startRow;
    const $limit =
      (payload.fetchOnlyWithoutFilters || isGroupChildrenRequest) && payload.request.range
        ? payload.request.range.endRow - payload.request.range.startRow
        : listViewState.range.endRow - listViewState.range.startRow;

    const organization: Organization = yield select(getOrganization);
    const [quickFilterCondition, filter] = extractSimpleConditionForFieldWithinTag(
      listViewState.filter,
      EntityType.ACTIVITY,
      ActivityFieldName.QUICK_FILTER
    );
    const $filters = {
      ...getRequestFlags(),
      ...convertToPlatformFilterModel(
        payload.fetchOnlyWithoutFilters ? createEmptyFilterModel() : filter,
        listViewState.columns,
        activityFieldModel
      ),
    };

    const isQuickSearchFilter = !payload.fetchOnlyWithoutFilters && !!quickFilterCondition;

    if (isQuickSearchFilter) {
      const { value } = quickFilterCondition;
      if (!("$and" in $filters)) {
        $filters.$and = [];
      }
      $filters.$and!.push({
        $or: [
          { name: { $in: value } },
          { "account.name": { $in: value } },
          { "contact.name": { $in: value } },
          { "deal.name": { $in: value } },
        ],
      });
    }

    if (isGroupChildrenRequest) {
      const parentActivityId = parseInt(payload.request.groupKeys![0], 10);
      if (!("$and" in $filters)) {
        $filters.$and = [];
      }
      $filters.$and!.push({ parentId: parentActivityId });
    }

    const hasRelationshipFilters = (Object.keys(listViewState.filter) as ActivityFieldName[]).some(
      (name) =>
        [ActivityFieldName.COMPANY, ActivityFieldName.DEAL, ActivityFieldName.PERSON].includes(name)
    );

    // We only fetch parent activities for the list view when there are no relationship filters
    if (!hasRelationshipFilters && !isQuickSearchFilter && !isGroupChildrenRequest) {
      if (!("$and" in $filters)) {
        $filters.$and = [];
      }
      $filters.$and!.push(getParentActivitiesFilter());
    }

    const requestPayload = {
      $filters,
      $limit,
      $offset,
      $order: convertToPlatformSortModel(listViewState.sort),
    };
    const response: ListResponse<Activity> = yield callApi(
      "fetchActivities",
      organization.id,
      requestPayload
    );
    if (payload.dataCallback) {
      yield call(payload.dataCallback, response);
    }
    yield put(
      fetchList.success(
        isGroupChildrenRequest
          ? undefined
          : {
              totalFilteredRecords: response.total,
              totalRecords: response.accessible,
            }
      )
    );
  } catch (error) {
    if (payload.failCallback) {
      yield call(payload.failCallback);
    }
    yield put(fetchList.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onUpdateActivity({ payload }: ReturnType<typeof updateActivity.request>) {
  try {
    const organization: Organization = yield select(getOrganization);
    const layoutId = activityLayoutModel.getLayoutFor(payload).id;

    const updatedActivity: Activity = yield callApi("updateActivity", organization.id, layoutId, {
      ...payload,
      files: (payload.files as RawFile[]).map(({ id }) => id),
    });
    yield put(updateActivity.success(updatedActivity));
    yield put(notifyAboutChanges({ entityType: EntityType.ACTIVITY, updated: [updatedActivity] }));

    notification.success({
      message: getSuccessNotificationNode(
        i18nService.getIntl(),
        i18nService.formatMessage(postponeSuccessMessage, "Activity postponed successfully")
      ),
    });
  } catch (error) {
    yield put(updateActivity.failure(payload.id));
    yield put(handleError({ error }));
  }
}

export function* onToggleComplete({ payload }: ReturnType<typeof toggleComplete.request>) {
  try {
    const organization: Organization = yield select(getOrganization);
    const layoutId = activityLayoutModel.getLayoutFor(payload).id;

    const response: Activity = yield callApi("updateActivity", organization.id, layoutId, {
      ...payload,
      completed: !payload.completed,
      files: payload.files ? (payload.files as RawFile[]).map(({ id }) => id) : undefined,
    });
    yield put(toggleComplete.success(response));
    yield put(notifyAboutChanges({ entityType: EntityType.ACTIVITY, updated: [response] }));

    notification.success({
      message: getSuccessNotificationNode(
        i18nService.getIntl(),
        i18nService.formatMessage(
          toggleCompleteSuccessMessage,
          `Activity marked as "${response.completed ? "Done" : "Not yet done"}"`,
          { completed: response.completed }
        )
      ),
    });
  } catch (error) {
    yield put(toggleComplete.failure(payload.id));
    yield put(handleError({ error }));
  }
}

export function* onOpenDuplicationModal({
  payload,
}: ReturnType<typeof openDuplicationModal.request>) {
  try {
    const organization: Organization = yield select(getOrganization);
    const { id, ...activity }: Activity = yield callApi("fetchActivity", organization.id, payload, {
      includeAccessStatus: true,
      includeCustomFields: true,
    });

    const filteredOutCustomFieldsEsKeys = activityFieldModel.fields
      .filter((field) => isCustomField(field) && (field.isUnique || isCalculatedField(field)))
      .map((customField) => customField.customFieldData.esKey);

    const filteredCustomFields = activity.customFields?.filter(
      ({ esKey }) => !filteredOutCustomFieldsEsKeys.includes(esKey)
    );

    yield put(openDuplicationModal.success(id));
    yield put(
      showGlobalCreateActivityModal({
        activity: { ...activity, customFields: filteredCustomFields },
        cloning: true,
      })
    );
  } catch (error) {
    yield put(toggleComplete.failure(payload));
    yield put(handleError({ error }));
  }
}

export function* onDeleteActivity({
  payload: { id, callback, deleteType },
}: ReturnType<typeof deleteActivity.request>) {
  try {
    const organization: Organization = yield select(getOrganization);
    yield callApi("deleteActivity", organization.id, id, deleteType);
    yield put(deleteActivity.success(id));
    if (callback) {
      yield call(callback);
    }
    yield put(notifyAboutChanges({ deletedIds: [id], entityType: EntityType.ACTIVITY }));

    notification.success({
      description: i18nService.formatMessage(successDeletedDescription),
      message: getSuccessNotificationNode(
        i18nService.getIntl(),
        i18nService.formatMessage(deleteSuccessMessage, "Activity successfully deleted")
      ),
    });
  } catch (error) {
    yield put(deleteActivity.failure(id));
    yield put(handleError({ error }));
  }
}

export function* onBulkDeleteActivities({
  payload,
}: ReturnType<typeof bulkDeleteActivities.request>) {
  try {
    const organization: Organization = yield select(getOrganization);
    const response: BulkDeleteResponse = yield callApi(
      "deleteEntities",
      organization.id,
      EntityType.ACTIVITY,
      payload.ids
    );

    const { deletedIds, failedCount } = parseBulkDeleteOutcome(payload.ids, response);

    yield put(bulkDeleteActivities.success());
    yield put(fetchList.request({ request: {} }));
    yield call(payload.onSuccess); // should go before reset selected due to closing modal/popup actions
    if (deletedIds.length) {
      yield put(notifyAboutChanges({ deletedIds, entityType: EntityType.ACTIVITY }));
    }

    const intl = i18nService.getIntl();
    const count = deletedIds.length;
    if (intl && failedCount) {
      const isPartialSuccess = count > 0;
      (isPartialSuccess ? notification.warn : notification.error)({
        description: isPartialSuccess
          ? i18nService.formatMessage(
              bulkDeletePartialDescription,
              `${failedCount} ${failedCount === 1 ? "activity" : "activities"} could not be deleted.`,
              { failedCount }
            )
          : undefined,
        message: isPartialSuccess
          ? i18nService.formatMessage(
              bulkDeletePartialMessage,
              `Only ${count} ${count === 1 ? "activity" : "activities"} deleted successfully`,
              { count }
            )
          : i18nService.formatMessage(
              bulkDeleteFailedMessage,
              `${failedCount} ${failedCount === 1 ? "activity was" : "activities were"} not deleted`,
              { count: failedCount }
            ),
      });
    } else if (intl) {
      notification.success({
        description: i18nService.formatMessage(
          bulkDeleteSuccessDescription,
          "To restore these records, go to Data in Settings.",
          {
            plural: count > 1,
          }
        ),
        message: getSuccessNotificationNode(
          intl,
          i18nService.formatMessage(
            bulkDeleteSuccessMessage,
            `${count === 1 ? "Activity" : "Activities"} successfully deleted`,
            { count }
          )
        ),
      });
    }
  } catch (error) {
    yield put(bulkDeleteActivities.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onFetchFilePreview({ payload }: ReturnType<typeof fetchFilePreview.request>) {
  try {
    const org: Organization = yield select(getOrganization);
    const fileData: Blob = yield callApi(
      "fetchFile",
      org.id,
      payload.file,
      false,
      true,
      EntityType.ACTIVITY,
      payload.activityId,
      { responseType: "blob" }
    );
    yield put(fetchFilePreview.success(fileData));
  } catch (error) {
    yield put(fetchFilePreview.failure());
    yield put(handleError({ error }));
  }
}

export function* onBulkEdit({ payload }: ReturnType<typeof bulkEdit.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    yield callApi("updateEntities", orgId, EntityType.ACTIVITY, payload.activities);

    const updatedActivities: ListResponse<Activity> = yield callApi("fetchActivities", orgId, {
      $filters: {
        ...getRequestFlags(),
        $and: [{ id: { $in: payload.activities.map(({ id }) => id) } }],
      },
    });

    yield put(bulkEdit.success(updatedActivities.data));

    const intl = i18nService.getIntl();
    const count = payload.activities.length;
    notification.success({
      message: getSuccessNotificationNode(
        intl,
        i18nService.formatMessage(
          bulkEditSuccessMessage,
          `${count} ${count === 1 ? "activity" : "activities"} updated`,
          {
            count,
          }
        )
      ),
    });

    yield put(
      notifyAboutChanges({ entityType: EntityType.ACTIVITY, updated: updatedActivities.data })
    );
    yield call(payload.onSuccess);
  } catch (error) {
    yield put(bulkEdit.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onBulkPostpone({ payload }: ReturnType<typeof bulkPostpone.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    yield callApi("updateEntities", orgId, EntityType.ACTIVITY, payload.activities);

    const updatedActivities: ListResponse<Activity> = yield callApi("fetchActivities", orgId, {
      $filters: {
        ...getRequestFlags(),
        $and: [{ id: { $in: payload.activities.map(({ id }) => id) } }],
      },
    });

    yield put(bulkPostpone.success(updatedActivities.data));

    const intl = i18nService.getIntl();
    const count = payload.activities.length;
    notification.success({
      message: getSuccessNotificationNode(
        intl,
        i18nService.formatMessage(
          bulkPostponeSuccessMessage,
          `${count} ${count === 1 ? "activity" : "activities"} postponed by ${payload.period}`,
          {
            count,
            period: payload.period,
          }
        )
      ),
    });
    yield put(
      notifyAboutChanges({ entityType: EntityType.ACTIVITY, updated: updatedActivities.data })
    );
  } catch (error) {
    yield put(bulkPostpone.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onBulkToggleComplete({ payload }: ReturnType<typeof bulkToggleComplete.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const requestPayload = payload.activities.map(({ id, layoutId }) => ({
      id,
      completed: payload.completed,
      completedAt: payload.completed ? new Date().toISOString() : null,
      layoutId,
    }));
    yield callApi("updateEntities", orgId, EntityType.ACTIVITY, requestPayload);

    const updatedActivities: ListResponse<Activity> = yield callApi("fetchActivities", orgId, {
      $filters: {
        ...getRequestFlags(),
        $and: [{ id: { $in: payload.activities.map(({ id }) => id) } }],
      },
    });

    yield put(bulkToggleComplete.success(updatedActivities.data));

    const intl = i18nService.getIntl();
    const count = payload.activities.length;
    notification.success({
      message: getSuccessNotificationNode(
        intl,
        i18nService.formatMessage(
          bulkToggleCompleteSuccessMessage,
          `${count} ${count === 1 ? "activity" : "activities"} marked as ${
            payload.completed ? '"Done"' : '"Not yet done"'
          }`,
          {
            completed: payload.completed,
            count,
          }
        )
      ),
    });
    yield put(
      notifyAboutChanges({
        entityType: EntityType.ACTIVITY,
        updated: updatedActivities.data,
      })
    );
  } catch (error) {
    yield put(bulkToggleComplete.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onUpdateNote({ payload }: ReturnType<typeof updateNote.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const layoutId = activityLayoutModel.getLayoutFor(payload.activity).id;
    const { files, ...activity } = payload.activity;

    const updatedActivity: Activity = yield callApi("updateActivity", orgId, layoutId, activity);
    yield put(updateNote.success({ id: updatedActivity.id, note: updatedActivity.note }));

    const intl = i18nService.getIntl();
    if (intl) {
      notification.success({
        message: intl.formatMessage(noteAddedSuccess, {
          text: payload.updated ? "Updated" : "Created",
        }),
      });
    }
    yield call(payload.onSuccess, updatedActivity);
  } catch (error) {
    yield put(updateNote.failure(payload.activity.id));
    yield put(handleError({ error }));
  }
}

export function* onUpdateActivityView({
  payload: { viewState },
}: ReturnType<typeof updateActivityView>) {
  const callback: (state: Partial<ViewState>) => undefined | void = yield select(
    getUpdateViewStateCallback
  );
  if (callback) {
    const currentViewState: ViewState = yield select(getListViewState);
    const { filter, ...restOfViewState } = viewState;
    const updatedViewState: ViewState = { ...currentViewState, ...restOfViewState };
    if (filter) {
      updatedViewState.filter = mergeNewFilterModelsOnRootLevel(currentViewState.filter, filter);
    }
    yield call(callback, updatedViewState);
  }
}

export function* onUploadActivityFiles({
  payload,
}: ReturnType<typeof uploadActivityFiles.request>) {
  try {
    const org: Organization = yield select(getOrganization);
    const fileGroupId = uuid.v4();
    const responses: SettleResult<RawFile>[] = yield allSettled(
      payload.files.map((file) => {
        return callApi("createFile", org.id, file, false, EntityType.ACTIVITY, payload.activityId, {
          headers: {
            "x-mmc-file-group-id": fileGroupId,
          },
        });
      })
    );
    const fileList: FileListItem[] = responses.map((response, index) => ({
      file: payload.files[index],
      uploading: false,
      ...(response.error
        ? { errored: true, errorMessage: String(response.result), exceedsLimit: false }
        : { errored: false, exceedsLimit: false, uploadedFile: response.result }),
    }));
    const filesUploadedSuccessfullyCount = fileList.reduce(
      (result, { errored }) => result + (errored ? 0 : 1),
      0
    );
    if (payload.callback) {
      yield call(payload.callback, fileList);
    }
    yield put(uploadActivityFiles.success(fileList));

    const updatedActivity: Activity = yield callApi("fetchActivity", org.id, payload.activityId, {
      ...getRequestFlags(),
    });
    yield put(notifyAboutChanges({ entityType: EntityType.ACTIVITY, updated: [updatedActivity] }));

    const intl = i18nService.getIntl();
    if (intl && filesUploadedSuccessfullyCount) {
      notification.success({
        message: intl.formatMessage(filesAddedSuccess, {
          count: filesUploadedSuccessfullyCount,
        }),
      });
    }
  } catch (error) {
    payload.callback?.(payload.files.map((file) => ({ errored: true, file, uploading: false })));
    yield put(uploadActivityFiles.failure());
    yield put(handleError({ error }));
  }
}

export function* onRemoveActivityFile({ payload }: ReturnType<typeof removeActivityFile.request>) {
  try {
    const org: Organization = yield select(getOrganization);

    yield callApi(
      "deleteEntityFile",
      org.id,
      EntityType.ACTIVITY,
      payload.activity.id,
      payload.file.id
    );
    const updatedActivity: Activity = yield callApi("fetchActivity", org.id, payload.activity.id, {
      ...getRequestFlags(),
    });
    yield put(removeActivityFile.success({ activity: updatedActivity, file: payload.file.id }));
    yield call(payload.onSuccess, updatedActivity);
    yield put(notifyAboutChanges({ entityType: EntityType.ACTIVITY, updated: [updatedActivity] }));
  } catch (error) {
    yield put(removeActivityFile.failure());
    yield put(handleError({ error }));
  }
}

export function* onExportActivities({
  payload: { viewState },
}: ReturnType<typeof exportActivities>) {
  const total: number = yield select(getTotalFilteredRecordsCount);
  yield put(exportEntities.request({ entityType: EntityType.ACTIVITY, total, viewState }));
}

export function* onFetchChildActivities({
  payload: { failureCallback, parentActivityIds, successCallback },
}: ReturnType<typeof fetchChildActivities.request>) {
  try {
    const showSharedActivities = activitySettings.getSharedActivitiesVisibility();

    const orgId: Organization["id"] = yield select(getOrganizationId);
    const response: ListResponse<Activity> = yield callApi("fetchActivities", orgId, {
      $filters: {
        $and: [{ parentId: { $in: parentActivityIds } }],
        includeAccessStatus: true,
        includeEmails: true,
        includeFiles: true,
        ...(showSharedActivities
          ? {
              includeAccountSharedActivities: true,
              includeContactSharedActivities: true,
              includeDealSharedActivities: true,
            }
          : {}),
      },
      $limit: 1000,
    });
    yield put(fetchChildActivities.success());
    yield call(successCallback, response.data);
  } catch (error) {
    yield call(failureCallback);
    yield put(handleError({ error }));
    yield put(fetchChildActivities.failure());
  }
}

export function* activitySaga() {
  yield takeEvery(bulkDeleteActivities.request, onBulkDeleteActivities);
  yield takeEvery(bulkEdit.request, onBulkEdit);
  yield takeEvery(bulkPostpone.request, onBulkPostpone);
  yield takeEvery(bulkToggleComplete.request, onBulkToggleComplete);
  yield takeEvery(deleteActivity.request, onDeleteActivity);
  yield takeEvery(fetchFilePreview.request, onFetchFilePreview);
  yield takeEvery(fetchList.request, onFetchList);
  yield takeEvery(openDuplicationModal.request, onOpenDuplicationModal);
  yield takeEvery(toggleComplete.request, onToggleComplete);
  yield takeEvery(updateActivity.request, onUpdateActivity);
  yield takeEvery(updateNote.request, onUpdateNote);
  yield takeLatest(removeActivityFile.request, onRemoveActivityFile);
  yield takeLatest(updateActivityView, onUpdateActivityView);
  yield takeEvery(uploadActivityFiles.request, onUploadActivityFiles);
  yield takeLatest(exportActivities, onExportActivities);
  yield takeLatest(fetchChildActivities.request, onFetchChildActivities);
}
